import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

const markdownComponents = {
  code: ({node, inline, className, children, ...props}) => {
    const match = /language-(\w+)/.exec(className || '');
    console.log('Code component called:', { 
      inline, 
      className, 
      match, 
      children: String(children),
      node: node
    });
    
    return !inline && match ? (
      <div className="my-4">
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.5',
            fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
          }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    ) : (
      <code 
        className={className} 
        style={{
          backgroundColor: '#2d3748',
          color: '#e2e8f0',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '0.9em',
          fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({node, children, ...props}) => (
    <div className="my-4">
      <pre
        style={{
          backgroundColor: '#282c34',
          color: '#abb2bf',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          lineHeight: '1.5',
          fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
          overflow: 'auto',
          border: '1px solid #3e4451',
        }}
        {...props}
      >
        {children}
      </pre>
    </div>
  ),
};

const MarkdownEditor = ({ value, onChange, placeholder }) => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="border border-pink-200 rounded-2xl overflow-hidden shadow-lg bg-white">
      {/* Toolbar */}
      <div className="bg-gray-50 px-4 py-2 border-b border-pink-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className={`px-3 py-1 rounded text-sm font-medium ${
              !showPreview 
                ? 'bg-pink-500 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Write
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className={`px-3 py-1 rounded text-sm font-medium ${
              showPreview 
                ? 'bg-pink-500 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Preview
          </button>
        </div>
        <div className="text-xs text-gray-500">
          {value.replace(/<[^>]*>/g, '').length} characters
        </div>
      </div>

      {/* Editor/Preview Area */}
      <div className="min-h-[400px]">
        {!showPreview ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || "Write your post content in Markdown...\n\n# Headers\n**Bold text**\n\n```python\nprint('Hello World')\n```"}
            className="w-full h-full min-h-[400px] p-4 resize-none border-none outline-none font-mono text-sm"
            style={{ fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace' }}
          />
        ) : (
          <div className="p-4 bg-gray-50 min-h-[400px] overflow-auto">
            {console.log('Preview content:', value)}
            {console.log('Content length:', value.length)}
            <ReactMarkdown 
              components={markdownComponents}
              remarkPlugins={[remarkGfm]}
            >
              {value}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-gray-50 px-4 py-2 border-t border-pink-200 text-xs text-gray-500">
        <div className="flex flex-wrap gap-4">
          <span>**bold** <span className="text-gray-400">|</span> *italic* <span className="text-gray-400">|</span> `code`</span>
          <span>```python <span className="text-gray-400">|</span> # Header</span>
        </div>
      </div>
    </div>
  );
};

export default MarkdownEditor; 